import React, { useState } from 'react';
import { FaBullseye, FaChartLine, FaClock, FaStar, FaChevronDown, FaChevronUp } from 'react-icons/fa';

const ObjectiveCard = ({ objective, selected = false, onSelect, priority = 1, expanded = false, onToggle }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const handleToggle = () => {
        setIsExpanded(!isExpanded);
        if (onToggle) onToggle();
    };

    const getDifficultyColor = (difficulty) => {
        switch (difficulty) {
            case 'easy':
                return 'bg-green-100 text-green-800';
            case 'medium':
                return 'bg-yellow-100 text-yellow-800';
            case 'hard':
                return 'bg-red-100 text-red-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'high':
                return 'bg-red-100 text-red-800';
            case 'medium':
                return 'bg-yellow-100 text-yellow-800';
            case 'low':
                return 'bg-green-100 text-green-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const getDifficultyLabel = (difficulty) => {
        switch (difficulty) {
            case 'easy':
                return 'Fácil';
            case 'medium':
                return 'Medio';
            case 'hard':
                return 'Difícil';
            default:
                return difficulty;
        }
    };

    const getPriorityLabel = (priority) => {
        switch (priority) {
            case 'high':
                return 'Alta';
            case 'medium':
                return 'Media';
            case 'low':
                return 'Baja';
            default:
                return priority;
        }
    };

    return (
        <div className={`
      relative bg-white rounded-lg shadow-lg border-2 transition-all duration-300 cursor-pointer
      ${selected
                ? 'border-blue-500 bg-blue-50 shadow-blue-200'
                : 'border-gray-200 hover:border-gray-300 hover:shadow-xl'
            }
    `}>
            {/* Priority Badge */}
            <div className="absolute top-4 right-4">
                <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                    {priority}
                </div>
            </div>

            {/* Header */}
            <div className="p-6 pb-4">
                <div className="flex items-start mb-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                        <FaBullseye className="text-blue-500 text-xl" />
                    </div>
                    <div className="flex-1">
                        <h3 className="text-lg font-bold text-gray-900 mb-2 leading-tight">
                            {objective.title}
                        </h3>
                        <p className="text-gray-600 text-sm leading-relaxed">
                            {objective.description}
                        </p>
                    </div>
                </div>

                {/* Key Info */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="flex items-center">
                        <FaChartLine className="text-green-500 mr-2" />
                        <div>
                            <div className="text-xs text-gray-500">Métrica Objetivo</div>
                            <div className="text-sm font-medium text-gray-900">{objective.targetMetric}</div>
                        </div>
                    </div>
                    <div className="flex items-center">
                        <FaClock className="text-blue-500 mr-2" />
                        <div>
                            <div className="text-xs text-gray-500">Timeline</div>
                            <div className="text-sm font-medium text-gray-900">{objective.timeframe}</div>
                        </div>
                    </div>
                </div>

                {/* Badges */}
                <div className="flex flex-wrap gap-2 mb-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getDifficultyColor(objective.difficulty)}`}>
                        {getDifficultyLabel(objective.difficulty)}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getPriorityColor(objective.priority)}`}>
                        Prioridad {getPriorityLabel(objective.priority)}
                    </span>
                </div>

                {/* Expected Impact */}
                <div className="bg-gray-50 rounded-lg p-3 mb-4">
                    <div className="flex items-center mb-2">
                        <FaStar className="text-yellow-500 mr-2" />
                        <span className="text-sm font-medium text-gray-900">Impacto Esperado</span>
                    </div>
                    <p className="text-sm text-gray-700">{objective.expectedImpact}</p>
                </div>
            </div>

            {/* Expandable Content */}
            <div className="border-t border-gray-200">
                <div
                    className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                    onClick={handleToggle}
                >
                    <span className="text-sm font-medium text-gray-700">Ver Razonamiento</span>
                    {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                </div>

                {isExpanded && (
                    <div className="px-4 pb-4">
                        <div className="bg-blue-50 rounded-lg p-4">
                            <h4 className="text-sm font-medium text-gray-900 mb-2">Razonamiento</h4>
                            <p className="text-sm text-gray-700 leading-relaxed">
                                {objective.reasoning}
                            </p>
                        </div>
                    </div>
                )}
            </div>

            {/* Selection Button */}
            <div className="p-4 pt-0">
                <button
                    onClick={() => onSelect(objective.id)}
                    className={`
            w-full py-3 px-4 rounded-lg font-medium transition-all duration-200
            ${selected
                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }
          `}
                >
                    {selected ? '✓ Seleccionado' : 'Seleccionar Objetivo'}
                </button>
            </div>
        </div>
    );
};

export default ObjectiveCard; 